import { DecryptModalComponent } from '../../../deployment/decrypt-modal/decrypt-modal.component';
import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { DeploymentService } from '../../core/services/deployment.service';
import {
  UploadBexFile,
  EUploadBexActions,
  UploadBexFileSuccess,
  GetBexHistory,
  GetBexHistorySuccess,
  DownloadTraceFile,
  DownloadTraceFileSuccess,
  GetCurrentBEXFromServer,
  GetCurrentBEXFromServerSuccess,
  GetObjectListMaintenanceReasonsSuccess,
  GetObjectListMaintenanceReasons,
  ApplyBexUpload,
  ApplyBexUploadSuccess,
  GetBexImportStatus,
  GetBexImportStatusSuccess,
  RemoveSelectedBexFile,
  CancelBexImport,
  CancelBexImportSuccess
} from '../actions/upload-bex.actions';
import { Store, select } from '@ngrx/store';
import { getCurrentBexFile } from '../selectors/upload-bex.selectors';
import { from, of } from 'rxjs';
import { UtilsService } from '../../../environment/services/utils.service';
import { map, switchMap, withLatestFrom, catchError, filter, finalize } from 'rxjs/operators';
import { BzgToastService, BzgModalService } from 'bz-genome-angular';
import { DatePipe } from '@angular/common';
import { WorkspaceMigrationModalComponent } from '../../../deployment/workspace-migration-modal/workspace-migration-modal.component';
import { saveFile } from 'app/app.module';
import { IAppState } from 'app/IAppState';

@Injectable()
export class UploadBexFileEffects {

  private static modalService;
  private static deploymentService;

  constructor(
    private _store: Store<IAppState>,
    private _deploymentService: DeploymentService,
    private _actions$: Actions,
    private _utilsService: UtilsService,
    private _toastService: BzgToastService,
    private _modalService: BzgModalService,
    private datePipe: DatePipe
  ) {
    UploadBexFileEffects.modalService = _modalService;
    UploadBexFileEffects.deploymentService = _deploymentService;
  }

  uploadBexFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(EUploadBexActions.UploadBexFile),
      withLatestFrom(this._store.pipe(select(getCurrentBexFile))),
      map(([action, bexFile]) => bexFile),
      switchMap((bexFile: File) => this._deploymentService.upload(bexFile)),
      switchMap((data: any) => [new UploadBexFileSuccess(data), new GetBexImportStatus()]),
      catchError((err, caught) => {
        this._store.dispatch(new RemoveSelectedBexFile());
        this._store.dispatch(new GetBexHistory());
        return caught;
      })
    )
  );

  getBexHistory$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetBexHistory>(EUploadBexActions.GetBexHistory),
      switchMap(() => this._deploymentService.getHistory()),
      switchMap((history: Array<any>) => of(new GetBexHistorySuccess(history)))
    )
  );

  downloadTraceFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType<DownloadTraceFile>(EUploadBexActions.DownloadTraceFile),
      map((action) => (action.payload)),
      switchMap((bexId) => this._deploymentService.downloadTraceFile(bexId)),
      switchMap((response: any) => {
        this.getFileFromResponse(response);
        return of(new DownloadTraceFileSuccess(response))
      })
    )
  );

  getCurrentBexOnServer$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetCurrentBEXFromServer>(EUploadBexActions.GetCurrentBEXFromServer),
      switchMap(() => this._deploymentService.getCurrentBEX()),
      switchMap((bex: ICurrentBexFromServer) => {
        if (bex.Error) {
          this.showToastError('<span>' + bex.Error.replace(/\r\n/g, '</span><span>') + '</span>');
        }
        return of(new GetCurrentBEXFromServerSuccess(bex));
      })
    )
  );

  getObjectListMaintenanceReasons$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetObjectListMaintenanceReasons>(EUploadBexActions.GetObjectListMaintenanceReasons),
      withLatestFrom(this._store.pipe(select(getCurrentBexFile))),
      map(([action, bexFile]) => bexFile),
      switchMap(async (bexFile) => {
        const response = await this._deploymentService.checkBexEncrypt().toPromise();
        let result = !response;

        if (response) {
          result = await UploadBexFileEffects.showModalDecryptBex({
            fileName: bexFile.name,
            fileDate: this.datePipe.transform(bexFile.lastModified, 'medium')
          });
        }

        if (result) {
          const reasons = await this._deploymentService.getMaintenanceWindowReasons().toPromise();
          return new GetObjectListMaintenanceReasonsSuccess(reasons);
        }
        else {
          this._store.dispatch(new RemoveSelectedBexFile());
          return new CancelBexImport();
        }
      }),
      catchError((err, caught) => {
        this._store.dispatch(new RemoveSelectedBexFile());
        this._store.dispatch(new GetBexHistory());
        return caught;
      })
    )
  );

  applyBexUpload$ = createEffect(() =>
    this._actions$.pipe(
      ofType<ApplyBexUpload>(EUploadBexActions.ApplyBexUpload),
      switchMap(async (data) => {
        const isShowingModalMigration = await this._deploymentService.showWorkspaceMigrationObject().toPromise();
        let workspaceMigrationSelection;

        if (isShowingModalMigration) {
          workspaceMigrationSelection = await UploadBexFileEffects.ShowModalWorkspaceMigration();
        } else {
          workspaceMigrationSelection = {
            cancel: false,
            action: false
          }
        }
        if (!workspaceMigrationSelection.cancel) {
          await this._deploymentService.applyBexUpload(workspaceMigrationSelection.action).toPromise();
        }
      }),
      switchMap((response: any) => {
        return from([new ApplyBexUploadSuccess(response), new GetCurrentBEXFromServer()]);
      })
    )
  );

  getBexStatusImport$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetBexImportStatus>(EUploadBexActions.GetBexImportStatus),
      switchMap(() => this._deploymentService.getImportStatus()),
      switchMap((response: string) => [new GetBexImportStatusSuccess(response)])
    )
  )

  cancelBexImport$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CancelBexImport>(EUploadBexActions.CancelBexImport),
      switchMap(() => this._deploymentService.cancelBexImport()),
      switchMap((response: any) => [new CancelBexImportSuccess(), new RemoveSelectedBexFile()])
    )
  )

  getFileFromResponse(result) {
    let fileName: string = this._utilsService.getFileNameFromHeaders(result.headers, 'previous_package.bex');
    fileName = _.replace(fileName, new RegExp('"', 'g'), '');
    if (navigator.appVersion.toString().indexOf('.NET') > 0) {
      saveFile(new Blob([result.body], { type: 'application/octet-stream' }), fileName);
    }
    else {
      const pdfUrl = (window.URL || window['webkitURL']).createObjectURL(new Blob([result.body], { type: 'application/octet-stream' }));
      const anchor = document.createElement('a');
      anchor.download = fileName;
      anchor.href = pdfUrl;
      anchor.click();
    }
  }

  public static async showModalDecryptBex(data) {
    const modal = this.modalService.open(
      DecryptModalComponent,
      {
        closeOnBackdropClick: false,
        closeOnRouterNavigation: true,
        width: '308px',
        data
      });

    return await modal.afterClosed().toPromise()
      .then(async (result) => {
        if (result) {
          await this.deploymentService.decryptBex(result.password, data.id).toPromise();
          return true;
        }
        return false;
      })
  }

  public static async ShowModalWorkspaceMigration() {
    const modal = this.modalService.open(
      WorkspaceMigrationModalComponent,
      {
        closeOnBackdropClick: false,
        closeOnRouterNavigation: true,
        width: '400px'
      }
    );

    return await modal.afterClosed().toPromise()
      .then(async (result) => {
        return result;
      });
  }

  showToastError(message: string) {
    this._toastService.open(
      message,
      {
        type: 'error',
        duration: 0,
        verticalPosition: 'bottom',
        horizontalPosition: 'right'
      });
  }
}
